import axios from "axios";
import { getImpersonationContext } from "./impersonation_storage";

export const AUTH_STORAGE_KEY = "authUser";

export interface AuthUserPayload {
  token: string;
  refreshToken: string;
  user: Record<string, unknown> | null;
}

const parseAuth = (raw: string | null): AuthUserPayload | null => {
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as AuthUserPayload;
  } catch {
    return null;
  }
};

/** Tenant session with opaque refresh — survives browser restart (BUG-F2-100). */
export const isDurableAuthSession = (auth: AuthUserPayload): boolean =>
  Boolean(auth.refreshToken);

export const getStoredAuth = (): AuthUserPayload | null => {
  const sessionAuth = parseAuth(sessionStorage.getItem(AUTH_STORAGE_KEY));
  const localAuth = parseAuth(localStorage.getItem(AUTH_STORAGE_KEY));

  // Impersonation / short-lived JWT stays tab-scoped in sessionStorage.
  if (
    sessionAuth &&
    (!isDurableAuthSession(sessionAuth) || getImpersonationContext() !== null)
  ) {
    return sessionAuth;
  }

  if (localAuth && isDurableAuthSession(localAuth)) {
    return localAuth;
  }

  // Migrate legacy durable sessions written to sessionStorage before BUG-F2-100.
  if (sessionAuth && isDurableAuthSession(sessionAuth)) {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(sessionAuth));
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
    return sessionAuth;
  }

  return localAuth ?? sessionAuth;
};

export const setStoredAuth = (auth: AuthUserPayload): void => {
  const durable =
    isDurableAuthSession(auth) && getImpersonationContext() === null;

  if (durable) {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  } else {
    sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
    // Impersonation must not leave a durable tenant JWT behind.
    if (!isDurableAuthSession(auth)) {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }

  applyAuthToken(auth.token);
};

export const clearStoredAuth = (): void => {
  sessionStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(AUTH_STORAGE_KEY);
  applyAuthToken(null);
};

export const applyAuthToken = (token: string | null): void => {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    return;
  }

  delete axios.defaults.headers.common.Authorization;
};
